import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { toast } from 'react-hot-toast';
import { formatCurrencyDollars } from '../../../../utils/common';
import { 
  ChartBarIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CogIcon,
  ShieldCheckIcon,
  XMarkIcon,
  InformationCircleIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

const UsageSection = ({ user, organization, userRole, toastStyle, darkMode }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [teamUsageData, setTeamUsageData] = useState(null);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Usage-based pricing settings
  const [pricingSettings, setPricingSettings] = useState({
    enabled: false,
    capType: 'capped',
    overageCapAmount: 20,
    enabledBy: null,
    enabledAt: null,
    lastModifiedBy: null,
    lastModifiedAt: null,
    agreementAccepted: false,
    agreementAcceptedAt: null,
    agreementAcceptedBy: null
  });
  const [savedPricingSettings, setSavedPricingSettings] = useState({});
  const [pricingChanges, setPricingChanges] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);
  const [showLimitlessModal, setShowLimitlessModal] = useState(false);
  const [showHighLimitOption, setShowHighLimitOption] = useState(false);
  const [tempHighLimit, setTempHighLimit] = useState(1000);
  
  // State for collapsible member details
  const [expandedMembers, setExpandedMembers] = useState(new Set());

  // Check if user can view team usage (admin or owner)
  const canViewTeamUsage = userRole === 'admin' || userRole === 'owner';

  useEffect(() => {
    if (canViewTeamUsage) {
      fetchTeamUsageData();
      fetchPricingSettings();
    } else {
      setIsLoading(false);
    }
  }, [canViewTeamUsage]);

  // Auto-refresh usage data every 30 seconds
  useEffect(() => {
    if (!canViewTeamUsage) return;
    
    const interval = setInterval(() => {
      fetchTeamUsageData();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [canViewTeamUsage]);

  // Refresh when user returns to the page/tab
  useEffect(() => {
    if (!canViewTeamUsage) return;
    
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchTeamUsageData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [canViewTeamUsage]);

  // Check for pricing changes
  useEffect(() => {
    const hasChanges = JSON.stringify(pricingSettings) !== JSON.stringify(savedPricingSettings);
    setPricingChanges(hasChanges);
  }, [pricingSettings, savedPricingSettings]);

  // Ensure minimum cap is respected when usage data changes
  useEffect(() => {
    if (teamUsageData?.tokenPooling?.estimatedOverageCost !== undefined && 
        savedPricingSettings.overageCapAmount && 
        pricingSettings.overageCapAmount === savedPricingSettings.overageCapAmount) {
      const currentOverage = teamUsageData.tokenPooling.estimatedOverageCost;
      const minimumCap = Math.max(20, Math.ceil(currentOverage));
      
      if (savedPricingSettings.overageCapAmount < minimumCap) {
        const updatedSettings = {
          ...savedPricingSettings,
          overageCapAmount: minimumCap
        };
        
        // Update both current and saved settings to prevent false change detection
        setPricingSettings(updatedSettings);
        setSavedPricingSettings(updatedSettings);
      }
    }
  }, [teamUsageData?.tokenPooling?.estimatedOverageCost, savedPricingSettings.overageCapAmount]);

  const fetchTeamUsageData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/team-billing/team-usage-details?_t=${Date.now()}`, // Cache buster
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('🔍 Fresh team usage data:', response.data);
      setTeamUsageData(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error fetching team usage data:', err);
      setError(err.response?.data?.message || 'Failed to fetch team usage data');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPricingSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/team-billing/usage-pricing-settings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setPricingSettings(response.data.settings);
        setSavedPricingSettings(response.data.settings);
      }
    } catch (err) {
      console.error('Error fetching pricing settings:', err);
      // Don't set error state, just use defaults
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handlePricingToggle = (enabled) => {
    setPricingSettings(prev => ({
      ...prev,
      enabled: enabled
    }));
  };

  const handleCapTypeChange = (capType) => {
    if (capType === 'limitless') {
      // If user already had limitless enabled before, skip the confirmation modal
      if (savedPricingSettings.capType === 'limitless' && savedPricingSettings.agreementAccepted) {
        setPricingSettings(prev => ({
          ...prev,
          capType: 'limitless',
          agreementAccepted: true
        }));
      } else {
        const currentOverage = teamUsageData?.tokenPooling?.estimatedOverageCost || 0;
        const minimumCap = Math.max(100, Math.ceil(currentOverage));
        setTempHighLimit(Math.max(minimumCap, 1000));
        setShowHighLimitOption(true);
        setShowLimitlessModal(true);
      }
    } else {
      setPricingSettings(prev => ({
        ...prev,
        capType: capType
      }));
    }
  };

  const handleOverageCapChange = (amount) => {
    const currentOverage = teamUsageData?.tokenPooling?.estimatedOverageCost || 0;
    const minimumCap = Math.max(20, Math.ceil(currentOverage));
    const value = Math.max(minimumCap, parseInt(amount) || minimumCap);
    setPricingSettings(prev => ({
      ...prev,
      overageCapAmount: value
    }));
  };

  const confirmLimitlessUsage = () => {
    setPricingSettings(prev => ({
      ...prev,
      capType: 'limitless',
      agreementAccepted: true
    }));
    setShowLimitlessModal(false);
    setShowHighLimitOption(false);
  };

  const setHighLimitInstead = () => {
    const currentOverage = teamUsageData?.tokenPooling?.estimatedOverageCost || 0;
    const minimumCap = Math.max(20, Math.ceil(currentOverage));
    const finalLimit = Math.max(minimumCap, tempHighLimit);
    
    setPricingSettings(prev => ({
      ...prev,
      capType: 'capped',
      overageCapAmount: finalLimit
    }));
    setShowLimitlessModal(false);
    setShowHighLimitOption(false);
  };

  const cancelLimitlessChange = () => {
    setShowLimitlessModal(false);
    setShowHighLimitOption(false);
  };

  const savePricingSettings = async () => {
    try {
      setSavingPricing(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/usage-pricing-settings`,
        {
          enabled: pricingSettings.enabled,
          capType: pricingSettings.capType,
          overageCapAmount: pricingSettings.overageCapAmount,
          agreementAccepted: pricingSettings.agreementAccepted
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setSavedPricingSettings(response.data.settings);
        setPricingSettings(response.data.settings);
        
        // Show success toast
        toast.success('Usage-based pricing settings updated successfully', toastStyle.success);
      }
    } catch (err) {
      console.error('Error saving pricing settings:', err);
      toast.error(err.response?.data?.message || 'Failed to save pricing settings', toastStyle.error);
    } finally {
      setSavingPricing(false);
    }
  };

  const toggleMemberExpansion = (userId) => {
    setExpandedMembers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
          <div className="account-modal-shape-lg p-4 bg-[#f5f5f5] dark:bg-[#222] rounded-2xl border border-[#e0e0e0] dark:border-[#2a2a2a]">
            <Skeleton height={20} width={150} className="mb-4" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Skeleton height={80} className="account-modal-shape" />
              <Skeleton height={80} className="account-modal-shape" />
              <Skeleton height={80} className="account-modal-shape" />
            </div>
            <Skeleton height={200} className="account-modal-shape" />
          </div>
        </SkeletonTheme>
      </div>
    );
  }

  if (!canViewTeamUsage) {
    return (
      <div className="space-y-6">
        <div className="account-modal-shape-lg p-4 bg-[#f5f5f5] dark:bg-[#222] rounded-2xl border border-[#e0e0e0] dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Usage Statistics</h3>
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-600 mb-4">
              <ExclamationTriangleIcon className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Access Restricted
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Only organization administrators and owners can view team usage statistics.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="account-modal-shape-lg p-4 bg-[#f5f5f5] dark:bg-[#222] rounded-2xl border border-[#e0e0e0] dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Usage Statistics</h3>
          <div className="text-center py-12">
            <div className="text-red-400 dark:text-red-500 mb-4">
              <ExclamationTriangleIcon className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-sm text-red-600 dark:text-red-400">
              {error}
            </p>
            <button
              onClick={fetchTeamUsageData}
              className="account-modal-btn mt-4 text-sm text-[#525252] dark:text-[#cccccc] hover:underline px-2 py-1 rounded-lg"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Usage-Based Pricing Management */}
      <div className="account-modal-shape-lg p-4 bg-[#f5f5f5] dark:bg-[#222] rounded-2xl border border-[#e0e0e0] dark:border-[#2a2a2a]">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-[#181818] dark:text-white">Usage-Based Pricing</h3>
          <div className="flex items-center space-x-3">
            {pricingChanges && (
              <button
                onClick={savePricingSettings}
                disabled={savingPricing}
                className="account-modal-btn inline-flex items-center px-3 py-1.5 border border-black text-xs font-medium rounded-lg text-white bg-black hover:bg-[#262626] focus:outline-none dark:bg-white dark:text-black dark:border-white dark:hover:bg-[#e0e0e0] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingPricing ? 'Saving...' : 'Save Changes'}
              </button>
            )}
            <button
              onClick={() => handlePricingToggle(!pricingSettings.enabled)}
              className={`${
                pricingSettings.enabled
                  ? 'bg-black focus:ring-[#181818] dark:bg-[#d4d4d4] dark:focus:ring-[#a3a3a3]'
                  : 'bg-[#d1d1d1] focus:ring-[#525252] dark:bg-[#404040]'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-[#222]`}
            >
              <span
                aria-hidden="true"
                className={`${
                  pricingSettings.enabled ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full shadow ring-0 transition duration-200 ease-in-out ${
                  pricingSettings.enabled
                    ? 'bg-white dark:bg-[#171717] dark:shadow-[0_1px_3px_rgba(0,0,0,0.45)]'
                    : 'bg-white dark:bg-[#fafafa] dark:shadow-[0_1px_2px_rgba(0,0,0,0.35)]'
                }`}
              />
            </button>
          </div>
        </div>

        {pricingSettings.enabled ? (
          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Capped Usage Option */}
              <div 
                className={`account-modal-item p-3 border rounded-lg cursor-pointer transition-all ${
                  pricingSettings.capType === 'capped'
                    ? 'border-black bg-[#ebebeb] dark:bg-[#272727] dark:border-[#404040]'
                    : 'border-[#e0e0e0] dark:border-[#404040] hover:border-[#b1b1b1] dark:hover:border-[#525252]'
                }`}
                onClick={() => handleCapTypeChange('capped')}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Capped Usage</h4>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    pricingSettings.capType === 'capped'
                      ? 'bg-black border-black dark:bg-white dark:border-white'
                      : 'border-[#d1d1d1] dark:border-[#404040]'
                  }`}>
                    {pricingSettings.capType === 'capped' && (
                      <CheckIcon className="w-2 h-2 text-white dark:text-black" />
                    )}
                  </div>
                </div>
                
                {pricingSettings.capType === 'capped' && (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-[#737373] dark:text-[#999999]">Max $</span>
                      <input
                        type="number"
                        min={Math.max(20, Math.ceil(teamUsageData?.tokenPooling?.estimatedOverageCost || 0))}
                        value={pricingSettings.overageCapAmount}
                        onChange={(e) => handleOverageCapChange(e.target.value)}
                        onKeyDown={(e) => {
                          const currentOverage = teamUsageData?.tokenPooling?.estimatedOverageCost || 0;
                          const minimumCap = Math.max(20, Math.ceil(currentOverage));
                          const currentValue = parseInt(e.target.value) || minimumCap;
                          
                          // Prevent down arrow if at minimum
                          if (e.key === 'ArrowDown' && currentValue <= minimumCap) {
                            e.preventDefault();
                          }
                        }}
                        onBlur={(e) => {
                          const currentOverage = teamUsageData?.tokenPooling?.estimatedOverageCost || 0;
                          const minimumCap = Math.max(20, Math.ceil(currentOverage));
                          const enteredValue = parseInt(e.target.value) || minimumCap;
                          
                          if (enteredValue < minimumCap) {
                            handleOverageCapChange(minimumCap.toString());
                          }
                        }}
                        className="account-modal-shape-sm w-20 px-2 py-1 text-sm border border-[#e0e0e0] dark:border-[#404040] rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white dark:bg-[#1a1a1a] dark:text-white"
                      />
                    </div>
                    {teamUsageData?.tokenPooling?.estimatedOverageCost > 0 && (
                      <p className="text-xs text-[#737373] dark:text-[#999999]">
                        Min {formatCurrencyDollars(Math.max(20, Math.ceil(teamUsageData.tokenPooling.estimatedOverageCost)), { minDecimals: 0, maxDecimals: 0 })}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Limitless Usage Option */}
              <div 
                className={`account-modal-item p-3 border rounded-lg cursor-pointer transition-all ${
                  pricingSettings.capType === 'limitless'
                    ? 'border-black bg-[#ebebeb] dark:bg-[#272727] dark:border-[#404040]'
                    : 'border-[#e0e0e0] dark:border-[#404040] hover:border-[#b1b1b1] dark:hover:border-[#525252]'
                }`}
                onClick={() => handleCapTypeChange('limitless')}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">Limitless Usage</h4>
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    pricingSettings.capType === 'limitless'
                      ? 'bg-black border-black dark:bg-white dark:border-white'
                      : 'border-[#d1d1d1] dark:border-[#404040]'
                  }`}>
                    {pricingSettings.capType === 'limitless' && (
                      <CheckIcon className="w-2 h-2 text-white dark:text-black" />
                    )}
                  </div>
                </div>
                <p className="text-xs text-[#737373] dark:text-[#999999]">No overage limits</p>
              </div>
            </div>

            {/* Current Settings Summary */}
           
          </div>
        ) : (
          <div className="account-modal-shape p-3 bg-[#f5f5f5] dark:bg-[#2a2a2a] rounded-lg">
            <p className="text-sm text-[#525252] dark:text-[#cccccc] mb-2">
              Members are blocked when they exceed plan limits.
            </p>
            <button
              onClick={() => handlePricingToggle(true)}
              className="account-modal-btn text-sm text-[#525252] hover:text-[#262626] dark:text-[#cccccc] dark:hover:text-white font-medium px-2 py-1 rounded-lg"
            >
              Enable
            </button>
          </div>
        )}
      </div>

      {/* Token Pooling Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="account-modal-shape-lg p-4 bg-[#f5f5f5] dark:bg-[#222] rounded-2xl border border-[#e0e0e0] dark:border-[#2a2a2a]">
          <div>
              <p className="text-sm font-medium text-[#737373] dark:text-[#999999]">Token Usage</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatCurrencyDollars(Math.min(teamUsageData?.tokenPooling?.monthlyUsage || 0, teamUsageData?.tokenPooling?.monthlyAllowance || 0))}
              </p>
              <div className="text-xs text-[#737373] dark:text-[#999999]">
                of {formatCurrencyDollars(teamUsageData?.tokenPooling?.monthlyAllowance || 0)} allowance
              </div>
              <div className="w-full bg-[#e0e0e0] rounded-full h-2 mt-2 dark:bg-[#404040]">
                <div 
                  className="bg-black dark:bg-white h-2 rounded-full transition-all duration-300" 
                  style={{ 
                    width: `${Math.min(100, ((teamUsageData?.tokenPooling?.monthlyUsage || 0) / (teamUsageData?.tokenPooling?.monthlyAllowance || 1)) * 100)}%` 
                  }}
                ></div>
              </div>
          </div>
        </div>

        <div className="account-modal-shape-lg p-4 bg-[#f5f5f5] dark:bg-[#222] rounded-2xl border border-[#e0e0e0] dark:border-[#2a2a2a]">
          <div>
              <p className="text-sm font-medium text-[#737373] dark:text-[#999999]">Active Members</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {teamUsageData?.totalMembers || 0}
              </p>
              <div className="text-xs text-[#737373] dark:text-[#999999]">
                Sharing pooled tokens
              </div>
          </div>
        </div>

        <div className="account-modal-shape-lg p-4 bg-[#f5f5f5] dark:bg-[#222] rounded-2xl border border-[#e0e0e0] dark:border-[#2a2a2a]">
          <div>
              <p className="text-sm font-medium text-[#737373] dark:text-[#999999]">Overage Spending</p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {formatCurrencyDollars(teamUsageData?.tokenPooling?.estimatedOverageCost || 0)}
              </p>
              <div className="text-xs text-[#737373] dark:text-[#999999]">
                {savedPricingSettings?.capType === 'limitless' ? (
                  'No overage limits'
                ) : (
                  `of ${formatCurrencyDollars(savedPricingSettings?.overageCapAmount || 20)} limit`
                )}
              </div>
              <div className="w-full bg-[#e0e0e0] rounded-full h-2 mt-2 dark:bg-[#404040]">
                <div 
                  className="bg-black dark:bg-white h-2 rounded-full transition-all duration-300" 
                  style={{ 
                    width: savedPricingSettings?.capType === 'limitless' ? '0%' : `${Math.min(100, ((teamUsageData?.tokenPooling?.estimatedOverageCost || 0) / (savedPricingSettings?.overageCapAmount || 20)) * 100)}%` 
                  }}
                ></div>
              </div>
          </div>
        </div>
      </div>

      {/* Team Member Usage header + Refresh */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-sm font-medium text-[#181818] dark:text-white">Team Member Usage</h3>
        <div className="flex items-center gap-3">
          {lastRefresh && (
            <span className="text-xs text-[#737373] dark:text-[#999999]">
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button
            onClick={fetchTeamUsageData}
            disabled={isLoading}
            className="account-modal-btn flex items-center gap-1 px-2 py-1 text-xs text-[#737373] hover:text-[#525252] dark:text-[#999999] dark:hover:text-[#cccccc] transition-colors disabled:opacity-50 rounded-lg"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Team Member Usage table */}
      {teamUsageData?.memberUsage?.length > 0 ? (
          <div className="account-modal-shape overflow-x-auto rounded-xl">
            <table className="w-full">
              <thead className="bg-[#ebebeb] dark:bg-[#1A1A1A]">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase tracking-wider">
                    Member
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase tracking-wider">
                    Requests
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase tracking-wider">
                    Tokens Used
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-[#737373] dark:text-[#999999] uppercase tracking-wider">
                    Last Activity
                  </th>
                </tr>
              </thead>
              <tbody className="bg-[#fafafa] dark:bg-[#222] divide-y divide-[#e0e0e0] dark:divide-[#404040]">
                {teamUsageData.memberUsage.map((member, index) => (
                  <React.Fragment key={member.userId || index}>
                    <tr 
                      className="account-modal-item hover:bg-[#ebebeb] dark:hover:bg-[#272727] cursor-pointer transition-colors"
                      onClick={() => toggleMemberExpansion(member.userId || index)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 mr-3">
                            {expandedMembers.has(member.userId || index) ? (
                              <ChevronDownIcon className="h-5 w-5 text-[#737373]" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5 text-[#737373]" />
                            )}
                          </div>
                          <div className="flex-shrink-0 h-8 w-8">
                            {member.profileImage?.url ? (
                              <img
                                className="h-8 w-8 rounded-full object-cover"
                                src={member.profileImage.url}
                                alt={member.name || member.email}
                              />
                            ) : (
                              <div className="h-8 w-8 rounded-full bg-[#d4e4f7] dark:bg-[#2a4365] flex items-center justify-center text-[#2563eb] dark:text-[#93c5fd] font-medium text-sm">
                                {member.name?.charAt(0)?.toUpperCase() || member.email?.charAt(0)?.toUpperCase() || '?'}
                              </div>
                            )}
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {member.name || 'Unknown'}
                            </div>
                            <div className="text-sm text-[#737373] dark:text-[#999999]">
                              {member.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {member.creditsUsed?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          requests
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {member.tokensUsed?.toLocaleString() || 0}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          tokens
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-[#737373] dark:text-[#999999]">
                        {formatDate(member.lastActivity)}
                      </td>
                    </tr>
                    
                    {/* Expanded Details Row */}
                    {expandedMembers.has(member.userId || index) && (
                      <tr className="">
                        <td colSpan="4" className="px-4 py-4">
                          <div className="space-y-4">
                            {/* Individual Messages */}
                            <div className="">
                              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Recent Messages</h4>
                              <div className="space-y-2 max-h-80 overflow-y-auto">
                                {member.messages && member.messages.length > 0 ? (
                                  member.messages.map((message, index) => (
                                    <div key={message.id || index} className="py-2 border-b border-gray-200 dark:border-[#404040] last:border-b-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                          {new Date(message.date).toLocaleDateString(undefined, { 
                                            month: 'short', 
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            timeZoneName: 'short'
                                          })}
                                        </span>
                                        <span className={`text-xs font-semibold ${
                                          message.kind === 'included' 
                                            ? 'text-black dark:text-white' 
                                            : 'text-black dark:text-white'
                                        }`}>
                                          {message.kind === 'included' ? 'Included' : 'Usage-based'}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 text-sm">
                                        <span className="text-gray-600 dark:text-gray-400 font-medium">{message.model}</span>
                                        {!message.model.includes('Image Generation') && (
                                          <>
                                            <span className="text-gray-400 dark:text-gray-500">•</span>
                                            <span className="text-gray-600 dark:text-gray-400">{message.totalTokens?.toLocaleString() || 0} tokens</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="text-center py-8">
                                    <div className="text-gray-400 dark:text-gray-600 mb-2">
                                      <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                      </svg>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">No messages found</p>
                                  </div>
                                )}
                              </div>
                            </div>


                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-gray-400 dark:text-gray-600 mb-4">
              <ChartBarIcon className="w-12 h-12 mx-auto" />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No usage data available yet
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
              Usage data will appear here as team members use the platform.
            </p>
          </div>
        )}

      {/* Plan Details */}
      {teamUsageData?.planDetails && (
        <div className="account-modal-shape-lg p-4 bg-[#f5f5f5] dark:bg-[#222] rounded-2xl border border-[#e0e0e0] dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-4">Plan Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Current Plan</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                {teamUsageData.planDetails.name}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Overage Billing</p>
              <p className="text-lg font-semibold text-gray-900 dark:text-white">
                At cost
              </p>
            </div>
          </div>
        </div>
      )}

       {/* Limitless Usage Confirmation Modal */}
       {showLimitlessModal && (
         <div className="fixed inset-0 bg-black/40 dark:bg-black/60 flex items-center justify-center z-50 p-4">
           <div className="account-modal-shape-xl bg-white dark:bg-[#1a1a1a] rounded-2xl border border-gray-200 dark:border-[#2a2a2a] p-6 max-w-md w-full shadow-xl relative">
             <button
               onClick={cancelLimitlessChange}
               className="account-modal-btn absolute top-4 right-4 p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-[#252525] transition-colors"
               aria-label="Close"
             >
               <XMarkIcon className="w-5 h-5" />
             </button>
             <div className="flex items-center space-x-3 mb-4 pr-8">
               <div className="flex-shrink-0">
                 <ExclamationTriangleIcon className="h-8 w-8 text-[#737373] dark:text-[#999999]" />
               </div>
               <div>
                 <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                   Enable Limitless?
                 </h3>
               </div>
             </div>
             
             <div className="mb-6">
               <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                 You will be charged for all overage usage with no cap.
               </p>
               
               <div className="account-modal-shape bg-gray-100 dark:bg-[#272727] p-3 rounded-lg mb-4 border border-gray-200 dark:border-[#404040]">
                 <p className="text-sm text-[#525252] dark:text-[#999999]">
                   May result in unexpected charges with high usage.
                 </p>
               </div>

               {showHighLimitOption && (
                 <div className="account-modal-shape bg-gray-100 dark:bg-[#272727] p-3 rounded-lg border border-gray-200 dark:border-[#404040]">
                   <p className="text-sm text-[#525252] dark:text-[#999999] mb-2">
                     <strong>Or</strong> set a high limit:
                   </p>
                   <div className="space-y-2">
                     <div className="flex items-center space-x-2">
                       <span className="text-sm text-[#525252] dark:text-[#999999]">Cap at $</span>
                                                <input
                           type="number"
                           min={Math.max(100, Math.ceil(teamUsageData?.tokenPooling?.estimatedOverageCost || 0))}
                           value={tempHighLimit}
                           onChange={(e) => {
                             const currentOverage = teamUsageData?.tokenPooling?.estimatedOverageCost || 0;
                             const minimumCap = Math.max(100, Math.ceil(currentOverage));
                             setTempHighLimit(Math.max(minimumCap, parseInt(e.target.value) || minimumCap));
                           }}
                           onKeyDown={(e) => {
                             const currentOverage = teamUsageData?.tokenPooling?.estimatedOverageCost || 0;
                             const minimumCap = Math.max(100, Math.ceil(currentOverage));
                             const currentValue = parseInt(e.target.value) || minimumCap;
                             
                             // Prevent down arrow if at minimum
                             if (e.key === 'ArrowDown' && currentValue <= minimumCap) {
                               e.preventDefault();
                             }
                           }}
                           onBlur={(e) => {
                             const currentOverage = teamUsageData?.tokenPooling?.estimatedOverageCost || 0;
                             const minimumCap = Math.max(100, Math.ceil(currentOverage));
                             const enteredValue = parseInt(e.target.value) || minimumCap;
                             
                             if (enteredValue < minimumCap) {
                               setTempHighLimit(minimumCap);
                             }
                           }}
                           className="account-modal-shape-sm w-24 px-2 py-1 text-sm border border-gray-200 dark:border-[#404040] rounded-lg focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white dark:bg-[#1a1a1a] dark:text-white"
                         />
                     </div>
                     {teamUsageData?.tokenPooling?.estimatedOverageCost > 0 && (
                       <p className="text-xs text-[#737373] dark:text-[#999999]">
                         Min {formatCurrencyDollars(Math.max(100, Math.ceil(teamUsageData.tokenPooling.estimatedOverageCost)), { minDecimals: 0, maxDecimals: 0 })}
                       </p>
                     )}
                   </div>
                 </div>
               )}
             </div>
             
             <div className="flex gap-3">
               {showHighLimitOption && (
                 <button
                   onClick={setHighLimitInstead}
                   className="account-modal-btn flex-1 px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-[#404040] text-[#181818] dark:text-white hover:bg-gray-100 dark:hover:bg-[#272727] transition-colors"
                 >
                   Cap at {formatCurrencyDollars(tempHighLimit, { minDecimals: 0, maxDecimals: 0 })}
                 </button>
               )}
               
               <button
                 onClick={confirmLimitlessUsage}
                 className="account-modal-btn flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-black dark:bg-white text-white dark:text-black hover:bg-[#262626] dark:hover:bg-[#e0e0e0] transition-colors"
               >
                 Limitless
               </button>
             </div>
           </div>
         </div>
       )}
     </div>
   );
};

export default UsageSection; 